import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';
import { api, clearApiSession, onSessionExpired } from '../api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const acknowledgeSessionExpiry = useCallback(() => setSessionExpired(false), []);

  useEffect(() => onSessionExpired(async () => {
    // Remove protected content immediately, even if server cleanup is offline.
    setSessionExpired(true);
    setUser(null);
    setBackendUser(null);
    setLoading(false);
    api.logout().catch(() => {});
    await signOut(auth).catch(() => {});
  }), []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.login();
      setBackendUser(data.user);
    } catch {
      setBackendUser(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setBackendUser(null);
      if (currentUser) {
        try {
          const data = await api.login();
          setBackendUser(data.user);
        } catch (e) {
          console.warn('Backend session creation failed:', e);
        }
      } else {
        clearApiSession();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
    const data = await api.login();
    setBackendUser(data.user);
    setSessionExpired(false);
  };

  const signup = async (email, password) => {
    await createUserWithEmailAndPassword(auth, email, password);
    const data = await api.login();
    setBackendUser(data.user);
    setSessionExpired(false);
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    await signOut(auth);
    setBackendUser(null);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const data = await api.login();
    setBackendUser(data.user);
    setSessionExpired(false);
    return result;
  };

  return (
    <AuthContext.Provider value={{ user, backendUser, loading, sessionExpired, acknowledgeSessionExpiry, login, signup, logout, signInWithGoogle, refreshUser }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
